import { useEffect, useState } from "react";
import { ArrowUUpLeft, Shield } from "@phosphor-icons/react";
import { toast } from "sonner";
import { getImpersonationMeta, stopImpersonation } from "../lib/impersonation";

/**
 * Sticky top banner that renders ONLY when the current session was
 * started via admin impersonation. Reads localStorage on mount; if
 * impersonation metadata exists, shows the target's name + a clear
 * "Return to admin" button.
 *
 * Designed to be impossible to miss — heavy red bar, sits above the
 * navbar — so an admin doesn't accidentally treat the impersonated
 * session as their own.
 */
export default function ImpersonationBanner() {
  const [meta, setMeta] = useState(() => getImpersonationMeta());
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    // Re-read on cross-tab updates (e.g., admin opened a second tab).
    const onStorage = () => setMeta(getImpersonationMeta());
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  if (!meta) return null;

  const target = meta.target || {};
  const displayName = target.truck_name || target.name || target.email || "owner";

  const handleStop = async () => {
    setExiting(true);
    try {
      await stopImpersonation();
      toast.success("Returned to your admin session.");
      // Hard reload so every cached request + provider context resets
      // to the admin user (cleaner than running through React state).
      window.location.href = "/admin";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not restore admin session — please log in again.");
      window.location.href = "/login";
    }
  };

  return (
    <div
      className="sticky top-0 z-[60] bg-rose-700 text-white text-sm shadow-md pt-[env(safe-area-inset-top)]"
      data-testid="impersonation-banner"
      role="status"
    >
      <div className="max-w-7xl mx-auto px-3 py-2 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Shield weight="fill" className="h-4 w-4 shrink-0" />
          <span className="font-semibold truncate">
            Impersonating <span className="underline decoration-white/40 underline-offset-2">{displayName}</span>
            <span className="hidden sm:inline text-white/80 font-normal ml-1.5">
              ({target.email || "—"})
            </span>
          </span>
        </div>
        <button
          type="button"
          onClick={handleStop}
          disabled={exiting}
          className="inline-flex items-center gap-1.5 rounded-full bg-white/15 hover:bg-white/25 disabled:opacity-60 disabled:cursor-not-allowed px-3 py-1 font-bold text-[12px] transition"
          data-testid="impersonation-banner-stop"
        >
          <ArrowUUpLeft weight="bold" className="h-3.5 w-3.5" />
          {exiting ? "Returning…" : "Return to admin"}
        </button>
      </div>
    </div>
  );
}
